import { Accessor, createMemo } from "solid-js";
import {
  HomeWebstoreTelescopeRouteData,
  SectionData,
} from "../data/home_webstore_telescope_route_data";
import { Product } from "~/server/types/search";

type BrandChunk = { type: "brands"; brands: Product[]; isFirst: boolean };
type SectionChunk = { type: "section"; section: SectionData };
type ContentChunk = BrandChunk | SectionChunk;

export function useHomeWebstoreTelescope(
  routeData: Accessor<HomeWebstoreTelescopeRouteData>
) {
  // memoized map of category name to category title
  const categoryNameToTitleMap = createMemo(() => {
    const categories = routeData()?.categories ?? [];
    return categories.data.reduce(
      (acc, category) => {
        acc[category.name] = category.metadata.title;
        return acc;
      },
      {} as Record<string, string>
    );
  });

  // Client-side interleaving logic
  const interleavedContent = createMemo(() => {
    const brands = routeData()?.brands ?? [];
    const sections = routeData()?.sections ?? [];

    const chunks: ContentChunk[] = [];
    let brandIndex = 0;
    let sectionIndex = 0;

    // First chunk of brands (12 brands with "Popular Brands" header)
    if (brands.length > 0) {
      const firstChunk = brands.slice(0, 12);
      chunks.push({ type: "brands", brands: firstChunk, isFirst: true });
      brandIndex = 12;
    }

    // Interleave sections and brand chunks
    while (sectionIndex < sections.length && brandIndex < brands.length) {
      // Add section
      chunks.push({ type: "section", section: sections[sectionIndex] });
      sectionIndex++;

      // Add next chunk of brands (12 brands)
      const nextChunk = brands.slice(brandIndex, brandIndex + 12);
      if (nextChunk.length > 0) {
        chunks.push({ type: "brands", brands: nextChunk, isFirst: false });
        brandIndex += 12;
      }
    }

    // All remaining brands go into a single chunk
    if (brandIndex < brands.length) {
      const remainingBrands = brands.slice(brandIndex);
      chunks.push({ type: "brands", brands: remainingBrands, isFirst: false });
    }

    return chunks;
  });

  return {
    interleavedContent,
    categoryNameToTitleMap,
  };
}
